import { useEffect, useRef } from 'react';

import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { picksToWire } from './board_wire.js';
import type { Picks } from './flight_multi_offers.js';

/**
 * O CONJUNTO de picks vai ao host a cada mudança (add, ✕, Desfazer, lote do
 * explorer — tudo passa pelo board), e no MOUNT quando o eco já traz pick (o
 * card `:f` re-declara o seu conjunto: sem isso o host guardaria o challengeId
 * do card anterior e o próximo filtro seria ignorado no servidor). Mount com
 * eco vazio cala (ruído); conjunto vazio DEPOIS de uma mudança despacha (✕ no
 * último pick precisa chegar). Congelado não fala: não há escolha viva a
 * declarar. Fora do card (teto de linhas do arquivo).
 *
 * O mount calado REGISTRA o conjunto vazio como já enviado, e é isso que
 * separa os dois casos: `dispatch` é instável (recriado pelo host a cada
 * render), então o effect reexecutava na 2ª render com o MESMO conjunto vazio
 * e, já sem o `first`, despachava `legs: []` sob o challengeId NOVO — o host
 * guardava um conjunto vazio para o card recém-chegado, o próximo texto livre
 * o levava junto e o servidor apagava o `perLeg[0].pick` que havia sobrevivido
 * (#3580). O ✕ no último pick continua chegando: a assinatura anterior a ele é
 * não vazia.
 */
export function useDispatchPicksToHost({
  picks,
  readOnly,
  blockId,
  challengeId,
  dispatch,
}: {
  picks: Picks;
  readOnly?: boolean;
  blockId?: string;
  challengeId: string;
  dispatch: GenUiDispatch;
}) {
  const declared = useRef(false);
  const lastSent = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (readOnly || !blockId) return;
    const first = !declared.current;
    declared.current = true;
    const legs = picksToWire(picks);
    // Gate pelo PAYLOAD do fio, não pelas deps do effect: `dispatch` é recriado
    // pelo host a cada render (função inline em `MessageRow.tsx`), então uma
    // dep completa reexecutaria isto a cada re-render do thread — sem o gate o
    // card re-despacharia o mesmo conjunto sem nenhuma mudança real de pick.
    const signature = `${challengeId}:${blockId}:${JSON.stringify(legs)}`;
    if (lastSent.current === signature) return;
    lastSent.current = signature;
    if (first && legs.length === 0) return;
    dispatch({ type: 'flight-multi-picks', challengeId, blockId, legs });
  }, [picks, readOnly, blockId, challengeId, dispatch]);
}
